import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { List, arrayMove } from "react-movable";

import config from "../../config";
import Footer from "./Footer";
import Header from "./Header";

const Order_allocation = () => {
  /* =========================================================
     USER
  ========================================================= */

  const [userData, setUserData] = useState(null);

  /* =========================================================
     STATE
     
     allocate:
     0 = disabled
     1 = cheapest
     2 = custom
  ========================================================= */

  const [allocate, setAllocate] = useState(0);
  const [carriers, setCarriers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [carrierLoading, setCarrierLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* =========================================================
     TOAST
  ========================================================= */

  const showToast = (success, message) => {
    Swal.fire({
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 3000,
      background: success ? "#3659cd" : "#a61c09",
      color: "white",
      title: message,
    });
  };

  /* =========================================================
     GET USER
  ========================================================= */

  useEffect(() => {
    const userDetail = localStorage.getItem("ship_rocket_user");

    if (!userDetail) {
      showToast(false, "User details not found");
      setLoading(false);
      return;
    }

    try {
      const parsedUser = JSON.parse(userDetail);
      setUserData(parsedUser);
    } catch (error) {
      console.error("User parse error:", error);
      showToast(false, "Invalid user details");
      setLoading(false);
    }
  }, []);

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    if (userData?.customer_id) {
      getCustomerDetail();
    }
  }, [userData]);

  /* =========================================================
     GET CUSTOMER ALLOCATION SETTINGS
  ========================================================= */

  const getCustomerDetail = async () => {
    try {
      setLoading(true);

      const dataToSend = {
        customer_id: userData.customer_id,
      };

      const url = `${config.apiUrl}/user/get_detail_customer`;

      const response = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      if (response.data?.status === true) {
        const output = response.data.output || {};

        /*
          Backend values:

          allocate = 0 -> disabled
          allocate = 1 -> cheapest
          allocate = 2 -> custom
        */

        const currentAllocate = Number(output.allocate || 0);

        setAllocate(currentAllocate);

        /*
          Only fetch carriers when custom allocation
          is already selected.
        */

        if (currentAllocate === 2) {
          await getCustomerCarriers();
        } else {
          setCarriers([]);
        }
      } else {
        showToast(false, "Unable to load allocation settings");
      }
    } catch (error) {
      console.error("getCustomerDetail error:", error);
      showToast(false, "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     GET CUSTOMER CARRIERS
  ========================================================= */

  const getCustomerCarriers = async () => {
    try {
      if (!userData?.customer_id) return;

      setCarrierLoading(true);

      const dataToSend = {
        customer_id: userData.customer_id,
      };

      const url = `${config.apiUrl}/user/get_customer_carriers`;

      const response = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      if (response.data?.status === true) {
        setCarriers(response.data.output || []);
      } else {
        setCarriers([]);
        showToast(false, "Unable to load couriers");
      }
    } catch (error) {
      console.error("getCustomerCarriers error:", error);
      setCarriers([]);
      showToast(false, "Unable to load couriers");
    } finally {
      setCarrierLoading(false);
    }
  };

  /* =========================================================
     ENABLE / DISABLE MASTER TOGGLE
  ========================================================= */

  const handleMasterToggle = async (e) => {
    const enabled = e.target.checked;

    /*
      If turning OFF:
      allocate = 0
    */

    if (!enabled) {
      await disableAllocation();
      return;
    }

    /*
      If turning ON:

      If current mode is already cheapest/custom,
      simply keep it.

      If current mode is 0, default to cheapest.
    */

    if (allocate === 0) {
      setAllocate(1);
      setCarriers([]);
    }
  };

  /* =========================================================
     DISABLE ALLOCATION
  ========================================================= */

  const disableAllocation = async () => {
    try {
      if (!userData?.customer_id) return;

      setSaving(true);

      const dataToSend = {
        customer_id: userData.customer_id,
        allocate: 0,
      };

      const url = `${config.apiUrl}/user/update_allocation_status`;

      const response = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      if (response.data?.status === true) {
        setAllocate(0);
        setCarriers([]);

        showToast(true, "Order allocation disabled successfully");
      } else {
        showToast(false, "Unable to disable allocation");
      }
    } catch (error) {
      console.error("disableAllocation error:", error);
      showToast(false, "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     CHEAPEST MODE
  ========================================================= */

  const handleCheapest = () => {
    setAllocate(1);
    setCarriers([]);
  };

  /* =========================================================
     CUSTOM MODE
  ========================================================= */

  const handleCustom = async () => {
    setAllocate(2);

    /*
      Load couriers every time custom mode is selected
      so we use the latest backend order.
    */

    await getCustomerCarriers();
  };

  /* =========================================================
     DRAG & DROP
  ========================================================= */

  const handleCarrierReorder = ({ oldIndex, newIndex }) => {
    setCarriers((current) => arrayMove(current, oldIndex, newIndex));
  };

  /* =========================================================
     SAVE SETTINGS
  ========================================================= */

  const saveSettings = async () => {
    try {
      if (!userData?.customer_id) {
        showToast(false, "Customer details not found");
        return;
      }

      /*
        If allocation is disabled,
        don't send carriers.
      */

      if (allocate === 0) {
        await disableAllocation();
        return;
      }

      /*
        Custom mode must have couriers.
      */

      if (allocate === 2 && carriers.length === 0) {
        showToast(false, "Please select at least one courier");
        return;
      }

      setSaving(true);

      const dataToSend = {
        customer_id: userData.customer_id,
        allocate: allocate,
        carriers: allocate === 2 ? carriers : [],
      };

      console.log("save allocation data =", dataToSend);

      const url = `${config.apiUrl}/user/save_allocation_carriers`;

      const response = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      if (response.data?.status === true) {
        showToast(true, "Allocation settings updated successfully");

        /*
          Reload from backend so UI always represents
          actual saved state.
        */

        await getCustomerDetail();
      } else {
        showToast(
          false,
          response.data?.message || "Unable to save allocation settings"
        );
      }
    } catch (error) {
      console.error("saveSettings error:", error);

      showToast(false, "Something went wrong while saving");
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     RESET
  ========================================================= */

  const handleReset = async () => {
    const result = await Swal.fire({
      title: "Disable Order Allocation?",
      text: "New orders will no longer be automatically allocated.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, disable",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    await disableAllocation();
  };

  /* =========================================================
     HELPERS
  ========================================================= */

  const isEnabled = allocate === 1 || allocate === 2;

  const isCheapest = allocate === 1;

  const isCustom = allocate === 2;

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div>
        <Header>
          <div className="content">
            <div
              style={{
                minHeight: "500px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div className="text-center">
                <div
                  className="spinner-border text-primary"
                  role="status"
                ></div>

                <div className="mt-2">Loading allocation settings...</div>
              </div>
            </div>
          </div>
        </Header>

        <Footer />
      </div>
    );
  }

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div>
      <Header>
        <div className="content">
          {/* =================================================
              PAGE HEADER
          ================================================= */}

          <div className="ol-header">
            <div className="ol-hd">
              <button
                type="button"
                className="ol-back"
                title="Go back"
                onClick={() => window.history.back()}
              >
                <i className="bi bi-arrow-left"></i>
              </button>

              <div>
                <div className="ol-title">Order Allocation Engine</div>

                <div className="ol-sub">
                  <span className="dot"></span>
                  Automation & Rules
                  <span
                    className="dot"
                    style={{
                      background: "var(--t4)",
                    }}
                  ></span>
                  Tools
                </div>
              </div>
            </div>

            <div className="ol-actions">
              <button
                type="button"
                className="ol-btn ghost"
                onClick={handleReset}
                disabled={saving}
              >
                <i className="bi bi-arrow-counterclockwise"></i>
                Reset
              </button>

              <button
                type="button"
                className="ol-btn primary"
                onClick={saveSettings}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>

          {/* =================================================
              INTRO
          ================================================= */}

          <div className="ig-hero">
            <span className="ig-eyebrow">
              <i className="bi bi-shuffle"></i>
              Automation & Rules
            </span>

            <h1 className="ig-title">Order Allocation Engine</h1>

            <p className="ig-desc">
              Decide how the system picks a courier for every order. Go fully
              automatic with the cheapest courier, or set your own priority list
              with drag & drop.
            </p>
          </div>

          <div className="oa-wrap">
            {/* =================================================
                MASTER TOGGLE
            ================================================= */}

            <div className="card-base oa-master">
              <span className="oa-master-ic">
                <i className="bi bi-power"></i>
              </span>

              <div className="oa-master-tx">
                <h2 className="oa-master-title">Enable Order Allocation</h2>

                <p className="oa-master-desc">
                  When enabled, the engine automatically assigns a courier to
                  every new order using the strategy below. Turn it off to ship
                  orders manually.
                </p>
              </div>

              <label className="oa-toggle" title="Toggle order allocation">
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={handleMasterToggle}
                  disabled={saving}
                />

                <span className="oa-toggle-slider"></span>
              </label>
            </div>

            {/* =================================================
                STATUS NOTE
            ================================================= */}

            {/* (Optional – you can uncomment if needed) */}

            {/* =================================================
                CONFIG
            ================================================= */}

            {isEnabled && (
              <div className="oa-config">
                {/* =================================================
                    STRATEGY
                ================================================= */}

                <div className="oa-block">
                  <div className="oa-block-head">
                    <div>
                      <h2 className="oa-block-title">
                        <i className="bi bi-sliders"></i>
                        Allocation Strategy
                      </h2>

                      <p className="oa-block-sub">
                        Choose how couriers are prioritised for each order.
                      </p>
                    </div>
                  </div>

                  <div className="oa-modes">
                    {/* ──────────────────────────────
                        CHEAPEST
                    ────────────────────────────── */}

                    <div
                      className={`ol-mode-card oa-mode ${
                        isCheapest ? "active" : ""
                      }`}
                      data-mode="cheap"
                      onClick={handleCheapest}
                      style={{
                        cursor: "pointer",
                        border: isCheapest
                          ? "2px solid #3D348B"
                          : "1px solid #e5e7eb",
                        backgroundColor: isCheapest ? "#f0f7ff" : "#fff",
                        borderRadius: "12px",
                        padding: "20px",
                        transition: "all 0.2s",
                      }}
                    >
                      <span
                        className="ol-mode-radio"
                        style={{
                          display: "inline-flex",
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          border: `2px solid ${
                            isCheapest ? "#0d6efd" : "#cbd5e1"
                          }`,
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "12px",
                          flexShrink: 0,
                        }}
                      >
                        {isCheapest && (
                          <span
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              background: "#0d6efd",
                              display: "block",
                            }}
                          />
                        )}
                      </span>

                      <span className="ol-mode-ic">
                        <i className="bi bi-cash-coin"></i>
                      </span>

                      <span className="ol-mode-tx">
                        <span className="ol-mode-title">
                          Cheapest First
                          <em className="ol-mode-rec">Recommended</em>
                        </span>

                        <span className="ol-mode-desc">
                          The engine always tries the lowest-cost courier that
                          can deliver the order, so you spend the least on
                          shipping.
                        </span>

                        <span className="ol-mode-feat">
                          <i className="bi bi-check2"></i>
                          Auto-sorted by estimated cost
                        </span>

                        <span className="ol-mode-feat">
                          <i className="bi bi-check2"></i>
                          No manual work needed
                        </span>
                      </span>
                    </div>

                    {/* ──────────────────────────────
                        CUSTOM
                    ────────────────────────────── */}

                    <div
                      className={`ol-mode-card oa-mode ${
                        isCustom ? "active" : ""
                      }`}
                      data-mode="custom"
                      onClick={handleCustom}
                      style={{
                        cursor: "pointer",
                        border: isCustom
                          ? "2px solid #3D348B"
                          : "1px solid #e5e7eb",
                        backgroundColor: isCustom ? "#f0f7ff" : "#fff",
                        borderRadius: "12px",
                        padding: "20px",
                        transition: "all 0.2s",
                      }}
                    >
                      <span
                        className="ol-mode-radio"
                        style={{
                          display: "inline-flex",
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          border: `2px solid ${
                            isCustom ? "#0d6efd" : "#cbd5e1"
                          }`,
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: "12px",
                          flexShrink: 0,
                        }}
                      >
                        {isCustom && (
                          <span
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              background: "#0d6efd",
                              display: "block",
                            }}
                          />
                        )}
                      </span>

                      <span className="ol-mode-ic">
                        <i className="bi bi-arrow-down-up"></i>
                      </span>

                      <span className="ol-mode-tx">
                        <span className="ol-mode-title">Custom Order</span>

                        <span className="ol-mode-desc">
                          Set your own priority by dragging couriers. Top of the
                          list is tried first, then it falls down to the next
                          one.
                        </span>

                        <span className="ol-mode-feat">
                          <i className="bi bi-check2"></i>
                          Drag & drop to reorder
                        </span>

                        <span className="ol-mode-feat">
                          <i className="bi bi-check2"></i>
                          Skip couriers with one tap
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    COURIER PRIORITY (only in custom mode)
                ================================================= */}

                {isCustom && (
                  <div className="oa-block">
                    <div className="oa-block-head">
                      <div>
                        <h2 className="oa-block-title">
                          <i className="bi bi-truck"></i>
                          Courier Priority
                        </h2>

                        <p className="oa-block-sub">
                          Drag and drop couriers to define their priority.
                        </p>
                      </div>
                    </div>

                    {/* ==========================================
                        LOADING
                    ========================================== */}

                    {carrierLoading ? (
                      <div className="card-base oa-list-card">
                        <div
                          className="text-center"
                          style={{
                            padding: "50px",
                          }}
                        >
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          ></div>

                          <div className="mt-2">Loading couriers...</div>
                        </div>
                      </div>
                    ) : carriers.length === 0 ? (
                      /* ==========================================
                         EMPTY
                      ========================================== */

                      <div className="card-base oa-list-card">
                        <div
                          className="text-center text-muted"
                          style={{
                            padding: "50px",
                          }}
                        >
                          <i
                            className="bi bi-truck"
                            style={{
                              fontSize: "35px",
                            }}
                          ></i>

                          <div className="mt-2">No couriers available</div>
                        </div>
                      </div>
                    ) : (
                      /* ==========================================
                         LIST
                      ========================================== */

                      <>
                        <div className="card-base oa-list-card">
                          <div className="oa-list">
                            <List
                              values={carriers}
                              onChange={handleCarrierReorder}
                              renderList={({ children, props }) => (
                                <div
                                  {...props}
                                  style={{
                                    ...props.style,
                                    padding: 0,
                                    margin: 0,
                                  }}
                                >
                                  {children}
                                </div>
                              )}
                              renderItem={({ value, props, index }) => {
                                const image = value.carrier_image
                                  ? `https://cdn.kalpcargo.com/images/${value.carrier_image}`
                                  : null;

                                return (
                                  <div
                                    {...props}
                                    className="mb-3"
                                    style={{
                                      ...props.style,
                                      cursor: "grab",
                                    }}
                                  >
                                    <div
                                      className="card"
                                      style={{
                                        background: "#fff",
                                        borderRadius: "12px",
                                        padding: "18px 20px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "15px",
                                        border: "1px solid #e5e7eb",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                                      }}
                                    >
                                      {/* PRIORITY */}

                                      <div
                                        style={{
                                          width: "35px",
                                          height: "35px",
                                          minWidth: "35px",
                                          borderRadius: "50%",
                                          background: "#f1f5f9",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {index + 1}
                                      </div>

                                      {/* IMAGE */}

                                      <div
                                        style={{
                                          width: "55px",
                                          height: "55px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {image ? (
                                          <img
                                            src={image}
                                            alt={
                                              value.carrier_name ||
                                              value.product_name ||
                                              "Carrier"
                                            }
                                            style={{
                                              maxWidth: "50px",
                                              maxHeight: "50px",
                                              objectFit: "contain",
                                            }}
                                          />
                                        ) : (
                                          <i
                                            className="bi bi-truck"
                                            style={{
                                              fontSize: "30px",
                                              color: "#64748b",
                                            }}
                                          ></i>
                                        )}
                                      </div>

                                      {/* DETAILS */}

                                      <div
                                        style={{
                                          flex: 1,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontWeight: "600",
                                            fontSize: "15px",
                                          }}
                                        >
                                          {value.product_name ||
                                            value.carrier_name ||
                                            "Courier"}
                                        </div>

                                        {value.carrier_name &&
                                          value.product_name &&
                                          value.carrier_name !==
                                            value.product_name && (
                                            <div
                                              style={{
                                                fontSize: "12px",
                                                color: "#64748b",
                                                marginTop: "3px",
                                              }}
                                            >
                                              {value.carrier_name}
                                            </div>
                                          )}
                                      </div>

                                      {/* DRAG ICON */}

                                      <div
                                        style={{
                                          color: "#94a3b8",
                                          fontSize: "20px",
                                        }}
                                      >
                                        <i className="bi bi-grip-vertical"></i>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }}
                            />
                          </div>
                        </div>

                        <p className="oa-foot">
                          <i className="bi bi-info-circle"></i>
                          Orders fall through the list: if the top courier can't
                          service an order, the next one is tried.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Header>

      <Footer />
    </div>
  );
};

export default Order_allocation;
